import React from 'react';

import _ from 'lodash';
import Card from './Card';
import Link from './Link';
import CompanyLogoPile from './CompanyLogoPile';
import Button, {ButtonSizes} from './ButtonNew';
import {StarIcon, ShortRightArrowIcon} from './svg';
import {clampLines, companyNameToTag} from '../util';

export const Placeholder = () => (
  <div className="flex flex-col mb-4 border border-gray-200 shadow rounded-lg overflow-hidden animate-pulse">
    <div className="w-full h-10 w-10 rounded-full bg-gray-100" />
    <div className="w-full shrink-0 p-5">
      <div className="h-8 bg-gray-100 rounded w-4/5 mb-2" />
      <div className="h-4 bg-gray-100 rounded w-full mb-2" />
      <div className="h-4 bg-gray-100 rounded w-full mb-4" />
      <div className="h-12 bg-gray-100 rounded w-full" />
    </div>
  </div>
);

const formatCoachRating = (rating) => {
  if (!rating || Number.isNaN(Number(rating))) return 0;
  return (Math.round(Number(rating) * 10) / 10).toFixed(1);
};

const shouldShowRating = (rating, reviews) => rating > 0 && reviews > 0;

const toLogoPileCompanies = (companies, tagMap) => _.chain(companies || [])
  .map((name) => {
    const tag = companyNameToTag(name, tagMap);
    return {
      id: tag?.id || name,
      name: tag?.name || name,
      imageUrl: tag?.image_url && `${tag.image_url}?w=32`,
    };
  })
  .uniqBy('id')
  .value();

const CoachCard = ({
  name,
  image,
  title,
  recommended,
  description,
  companies = [],
  skills,
  rating,
  numSessions,
  href,
  onClick,
  tagMap = {},
}) => (
  <Card
    className="relative flex flex-col overflow-hidden cursor-pointer hover:shadow-md transition duration-50 group"
    onClick={onClick}
    border
  >
    <div className="flex-col items-center w-full">
      <div className="flex relative mb-2">
        <img
          className="h-16 w-16 mr-4 object-cover rounded-full shrink-0"
          src={image}
          alt={name}
          loading="lazy"
        />
      </div>
      {recommended && (
        <div className="absolute top-4 right-4 inline-flex items-center gap-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-lg px-2 py-1">
          <StarIcon className="w-3 h-3 text-indigo-600" />
          Recommended
        </div>
      )}
    </div>
    <div className="flex flex-col justify-between w-full h-full">
      <div className="flex flex-col">
        <div className="flex items-center gap-2">
          <Link href={href} onClick={onClick}>
            <h3 className="text-xl font-bold">
              {name}
            </h3>
          </Link>
          <CompanyLogoPile
            className="shrink-0"
            companies={toLogoPileCompanies(companies, tagMap)}
          />
        </div>
        <span className="mb-2 text-base font-medium text-gray-500 leading-tight">
          {title}
        </span>

        {shouldShowRating(rating, numSessions) && (
          <div className="flex items-center">
            <StarIcon className="h-5 w-5 text-yellow-400 -ml-0.5" />
            <span className="ml-0.5 flex items-center text-gray-600 text-sm font-semibold gap-1">
              {`${formatCoachRating(rating)} rating`}
              {' '}
              <span className="font-light text-gray-500">
                {numSessions > 1 && `(${numSessions} sessions)`}
              </span>
            </span>
          </div>
        )}

        <span className="my-2 mb-4 text-gray-500 font-medium" style={clampLines(2)}>
          {description}
        </span>

        <div className="mb-2 flex flex-wrap gap-2 items-center text-gray-700 font-medium text-sm  whitespace-nowrap">
          {skills?.slice(0, 2).map((skill) => (
            <div className="bg-gray-200 text-gray-600 text-xs font-medium rounded-lg px-2 py-1" key={skill}>
              {tagMap[skill]?.name}
            </div>
          ))}
          {skills?.length > 2 && (
            <div className="bg-gray-200 text-gray-600 text-xs font-medium rounded-lg px-2 py-1">
              {`+ ${skills.length - 2} more`}
            </div>
          )}
        </div>
      </div>
      <Button
        size={ButtonSizes.LARGE}
        className="w-full mt-4"
        href={href}
        onClick={onClick}
      >
        Book now
        <ShortRightArrowIcon stroke="#ffffff" className="w-3 ml-1.5" />
      </Button>
    </div>
  </Card>
);

export default CoachCard;

